import { readConsentCookie, writeConsentCookie } from "./cookie.js";
import {
  ConsentStatus,
  ConsentCategories,
  DEFAULT_CONSENT_PREFERENCES,
} from "./constants.js";

/**
 * The semantic consent API — the single source of truth for browser consent
 * across all three Agapaé surfaces.
 *
 * Two reads, deliberately distinct:
 *   - `getStoredConsent()` answers "has the visitor answered, and with what?"
 *     (`null` when they have not). This is what a banner needs.
 *   - `getConsent()` answers "what am I allowed to do right now?" and ALWAYS
 *     returns a usable record, falling back to the deny-by-default state. This
 *     is what a future gate needs — it can never accidentally read `null` and
 *     treat it as permission.
 *
 * Phase 7.0A ships this module and moves the banner onto it. Nothing gates on
 * consent yet, and nothing here talks to GTM — see
 * docs/analytics/consent-foundation.md.
 */

/**
 * Consent-change subscribers. Module-scoped so a subscription survives the
 * mount/unmount of whatever component registered it — the same reasoning as the
 * module-scoped `firedSections` guard in the analytics foundation.
 *
 * Each vendored copy owns its own registry: `landing-page`, `invite`, and `app`
 * are separate bundles on separate origins, so a listener registered on one
 * surface is never expected to hear a write made on another. Cross-surface
 * propagation happens through the shared `.agapae.gr` cookie on the next page
 * load, not through this set.
 *
 * @type {Set<import("./types.js").ConsentListener>}
 */
const listeners = new Set();

/**
 * The deny-by-default consent record for a visitor who has not answered yet.
 * A FRESH object every call — `DEFAULT_CONSENT_PREFERENCES` is frozen, and
 * handing out the frozen reference would let a consumer's state container
 * alias a module constant.
 *
 * @returns {import("./types.js").ConsentState}
 */
export function getDefaultConsent() {
  return {
    status: ConsentStatus.UNKNOWN,
    preferences: { ...DEFAULT_CONSENT_PREFERENCES },
    date: null,
  };
}

/**
 * The stored consent record, or `null` when the visitor has not answered.
 *
 * Returns the parsed cookie as-is: `JSON.parse` hands back a new object on every
 * call, so there is no shared mutable state to defend against, and no coercion
 * that could turn an unusable stored value into a truthy one.
 *
 * @returns {import("./types.js").ConsentState | null}
 */
export function getStoredConsent() {
  return readConsentCookie();
}

/**
 * Whether the visitor has answered the consent prompt at all.
 * The inverse of this is what decides whether a banner is shown.
 *
 * @returns {boolean}
 */
export function isConsentAnswered() {
  return Boolean(getStoredConsent());
}

/**
 * The EFFECTIVE consent state — always usable, never `null`.
 *
 * An unanswered visitor reads as the deny-by-default record. A partial or
 * legacy stored record is normalized field by field against that default, so a
 * consumer never has to defend against a half-written cookie.
 *
 * @returns {import("./types.js").ConsentState}
 */
export function getConsent() {
  const stored = getStoredConsent();
  if (!stored) return getDefaultConsent();

  const fallback = getDefaultConsent();
  return {
    status: stored.status || fallback.status,
    preferences: stored.preferences || fallback.preferences,
    date: stored.date || null,
  };
}

/**
 * Whether a specific category is granted right now.
 *
 * Deny-by-default in every ambiguous case: unanswered visitor, missing
 * preferences, or an unrecognized category name all return `false`. Only an
 * explicit `true` in the record grants.
 *
 * @param {string} category  A `ConsentCategories` value.
 * @returns {boolean}
 */
export function hasConsent(category) {
  return getConsent().preferences[category] === true;
}

/**
 * Subscribe to consent changes. The listener fires after every successful
 * `setConsent()` (including the semantic wrappers below) with the newly written
 * record — never on the initial read, and never on a no-op.
 *
 * @param {import("./types.js").ConsentListener} listener
 * @returns {() => void} Unsubscribe. Safe to call more than once.
 */
export function subscribeToConsent(listener) {
  if (typeof listener !== "function") return () => {};
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Notify subscribers in isolation: one listener throwing must never prevent the
 * others from running, and must never surface to the code that recorded the
 * consent. Same discipline as the backend analytics core's provider fan-out
 * (api/services/analytics/index.mjs) — a broken observer cannot break the write.
 *
 * @param {import("./types.js").ConsentState} state
 * @returns {void}
 */
function notifyListeners(state) {
  for (const listener of listeners) {
    try {
      listener(state);
    } catch (err) {
      console.warn("[consent][listener-failed]", err?.message);
    }
  }
}

/**
 * Record a consent decision: stamp it with the moment it was made, persist it,
 * then notify subscribers.
 *
 * Prefer the semantic wrappers (`acceptAllConsent` / `rejectNonEssentialConsent`)
 * — they own what each banner choice MEANS in terms of categories, so that
 * policy lives here rather than in a component.
 *
 * @param {"accepted"|"rejected"} status
 * @param {import("./types.js").ConsentPreferences} preferences
 * @returns {import("./types.js").ConsentState} The record as written.
 */
export function setConsent(status, preferences) {
  /** @type {import("./types.js").ConsentState} */
  const state = {
    status,
    // Copied so the persisted record can't be mutated through the caller's
    // reference after the fact.
    preferences: { ...preferences },
    date: new Date().toISOString(),
  };

  writeConsentCookie(state);
  notifyListeners(state);
  return state;
}

/**
 * "Accept all" — every category granted.
 *
 * @returns {import("./types.js").ConsentState} The record as written.
 */
export function acceptAllConsent() {
  return setConsent(ConsentStatus.ACCEPTED, {
    [ConsentCategories.ESSENTIAL]: true,
    [ConsentCategories.ANALYTICS]: true,
    [ConsentCategories.MARKETING]: true,
  });
}

/**
 * "Refuse" — essential only. Identical categories to the deny-by-default state,
 * but recorded as a deliberate `rejected` answer rather than silence, which is
 * the distinction that stops the banner re-prompting.
 *
 * @returns {import("./types.js").ConsentState} The record as written.
 */
export function rejectNonEssentialConsent() {
  return setConsent(ConsentStatus.REJECTED, { ...DEFAULT_CONSENT_PREFERENCES });
}
